import React, { useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import { format, isSaturday, isSunday } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { storageController } from '../../controllers/StorageController';
import { FormRouteEnum } from '../../routes/FormRoutes';
import { useFormRouting } from '../../routes/FormRoutes';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import ScheduleCallForm from './ScheduleCallForm';
import { isHoliday } from '../../utils/businessHours';
import { getBrowserTimezone } from '../../utils/timezones';
import ErrorMessage from '../ui/ErrorMessage';
import { useHubspot } from '../../hooks/useHubspot';
import { formatCurrency } from '../../utils/formatting';
import { TCPACompliance } from '../../utils/tcpaCompliance';
import { selectVariant } from './success-step/config';
import { SuccessStepContext, ContactCenterAvailability } from './success-step/types';
import { createComponentRenderer } from './success-step/renderers';
import { shouldAutoOpenScheduler } from './success-step/urlParams';
import { clientConfig } from '../../config/clientConfig';
import { simulationService } from '../../services/simulation';

type SuccessStepProps = {
  contactCenterAvailability: ContactCenterAvailability;
  shouldSendCall?: boolean;
  onScheduleAppointment?: (time: Date) => Promise<boolean>;
  onInitiateCall: () => Promise<{ success: boolean; from?: string; error?: string }>;
  isBpoAgent?: boolean;
  onVariantShown?: (variantId: string) => void; // Callback to notify parent of variant shown
};

export default function SuccessStep({ 
  contactCenterAvailability, 
  shouldSendCall = false,
  onScheduleAppointment,
  onInitiateCall,
  isBpoAgent = false,
  onVariantShown
}: SuccessStepProps) {
  const { navigateToRoute } = useFormRouting();
  const applicationData = storageController.getApplicationData();
  const customerPhoneNumber = applicationData?.phone || '';
  const firstName = storageController.getContactFirstName() || '';
  const debtAmount = storageController.getDebtAmount();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(storageController.getScheduledTime());
  const [isCallInitiated, setIsCallInitiated] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(() => shouldAutoOpenScheduler());
  const [showScheduleButton, setShowScheduleButton] = useState(false);
  const [showMissedCallMessage, setShowMissedCallMessage] = useState(false);
  const hubspot = useHubspot();

  // Track if we've already reported the initial variant (avoid duplicate tracking)
  // This MUST be before any conditional returns
  const hasTrackedRef = React.useRef(false);

  // Build context for variant selection
  const today = new Date();

  const simulationOverrides = clientConfig.shouldMockServices
    ? {
        tcpa: simulationService.shouldOverrideTcpaHours(),
        weekend: simulationService.shouldOverrideWeekend(),
        holiday: simulationService.shouldOverrideHoliday(),
        bpo: simulationService.shouldOverrideBpoAgent(),
        callInitiated: simulationService.shouldOverrideCallInitiated(),
        scheduledTime: simulationService.shouldOverrideScheduledTime(),
        shouldSendCall: simulationService.shouldOverrideShouldSendCall()
      }
    : {
        tcpa: { override: false, isWithin: false },
        weekend: { override: false, isWeekend: false },
        holiday: { override: false, isHoliday: false },
        bpo: { override: false, isBpoAgent: false },
        callInitiated: { override: false, isCallInitiated: false },
        scheduledTime: { override: false, scheduledTime: null as null },
        shouldSendCall: { override: false, shouldSendCall: false }
      };
  
  const context: SuccessStepContext = {
    contactCenterAvailability,
    isWeekend: simulationOverrides.weekend.override ? simulationOverrides.weekend.isWeekend : (isSaturday(today) || isSunday(today)),
    isHoliday: simulationOverrides.holiday.override ? simulationOverrides.holiday.isHoliday : isHoliday(today),
    isWithinCallHours: simulationOverrides.tcpa.override ? simulationOverrides.tcpa.isWithin : TCPACompliance.isWithinCallHours(),
    isBpoAgent: simulationOverrides.bpo.override ? simulationOverrides.bpo.isBpoAgent : isBpoAgent,
    isCallInitiated: simulationOverrides.callInitiated.override ? simulationOverrides.callInitiated.isCallInitiated : isCallInitiated,
    scheduledTime: simulationOverrides.scheduledTime.override ? simulationOverrides.scheduledTime.scheduledTime : scheduledTime,
    showScheduleButton,
    showMissedCallMessage,
    shouldSendCall: simulationOverrides.shouldSendCall.override
      ? simulationOverrides.shouldSendCall.shouldSendCall
      : shouldSendCall,
  };

  // Select variant based on context (before any returns)
  const variant = contactCenterAvailability !== null ? selectVariant(context) : null;
  
  // Notify parent of variant shown for analytics - ONLY ONCE for initial variant
  // This useEffect MUST be before any conditional returns
  React.useEffect(() => {
    if (contactCenterAvailability !== null && 
        onVariantShown && 
        !hasTrackedRef.current &&
        variant) {
      // Skip tracking intermediate UI states (these happen AFTER user actions)
      // Backend already knows from API calls (scheduleAppointment, initiateCall)
      const isIntermediateState = 
        variant.id === 'appointment-scheduled' || 
        variant.id === 'call-initiated';
      
      if (!isIntermediateState) {
        onVariantShown(variant.id);
        hasTrackedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant?.id, contactCenterAvailability, onVariantShown]);

  // Handle delayed schedule button (if variant specifies a delay)
  React.useEffect(() => {
    if (variant?.delayScheduleButton && !showScheduleButton) {
      const timer = setTimeout(() => {
        setShowScheduleButton(true);
      }, variant.delayScheduleButton);

      return () => clearTimeout(timer);
    }
  }, [variant?.id, variant?.delayScheduleButton, showScheduleButton]);

  // Handle delayed missed call message (if variant specifies a delay)
  React.useEffect(() => {
    if (variant?.delayMissedCallMessage && !showMissedCallMessage) {
      const timer = setTimeout(() => {
        setShowMissedCallMessage(true);
      }, variant.delayMissedCallMessage);

      return () => clearTimeout(timer);
    }
  }, [variant?.id, variant?.delayMissedCallMessage, showMissedCallMessage]);

  // Redirect if not successful
  if (!storageController.isApplicationSuccessful()) {
    navigateToRoute(FormRouteEnum.START);
    return null;
  }

  const formatAppointmentTime = (date: Date): string => {
    const timezone = getBrowserTimezone();
    const zonedDate = utcToZonedTime(date, timezone);
    return format(zonedDate, "EEEE, MMMM d 'at' h:mm a");
  };

  const handleScheduleCall = async (selectedTime: Date) => {
    setLoading(true);
    setError(null);

    try {
      if (onScheduleAppointment) {
        const success = await onScheduleAppointment(selectedTime);
        
        if (success) {
          storageController.setScheduledTime(selectedTime);
          setScheduledTime(selectedTime);
          setShowScheduleForm(false);

          hubspot.trackEvent('appointment_scheduled', {
            email: applicationData?.email,
            form_step: 'success',
            scheduled_time: selectedTime.toISOString(),
            timezone: getBrowserTimezone()
          });
        } else {
          throw new Error('Failed to schedule appointment');
        }
      }
    } catch (error) {
      console.error('Failed to schedule call:', error);
      setError('Unable to schedule your call. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const handleCallInitiated = () => {
    setIsCallInitiated(true);
  };

  const handleCountdownComplete = () => {
    setShowScheduleButton(true);
  };

  // Show loading state while checking availability
  if (contactCenterAvailability === null) {
    return (
      <div id="success-step-loading" className="text-center space-y-6">
        <div className="w-16 h-16 bg-[#b3905e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <div className="relative">
            <CheckCircle className="w-8 h-8 text-[#b3905e]" />
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5">
              <Loader2 className="w-4 h-4 text-[#b3905e] animate-spin" />
            </div>
          </div>
        </div>
        
        <Title 
          id="success-step-loading-title"
          name="success-step-loading-title"
          as="h1" 
          className="mb-2"
        >
          Application Received!
        </Title>
        
        <div className="max-w-sm mx-auto">
          <Description 
            id="success-step-loading-description"
            name="success-step-loading-description"
            size="lg"
          >
            Thank you <span className="font-medium">{firstName}</span>, your application has been received. We're checking our team's availability to assist you...
          </Description>
        </div>

        <div className="w-full md:w-1/2 mx-auto">
          <div className="bg-[#b3905e]/5 rounded-lg p-6">
            <div className="flex items-center justify-center gap-3 text-[#b3905e] animate-pulse">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="font-medium">Checking availability</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Variant was already selected at the top, just use it here
  if (!variant) {
    // This shouldn't happen, but safety check
    return null;
  }
  
  // Show scheduler if explicitly requested OR if availability is 'none' (match old behavior)
  if ((showScheduleForm || contactCenterAvailability === 'none') && !scheduledTime) {
    return (
      <>
        <div id="success-step-schedule" className="text-center mb-6">
          <Title 
            id="success-step-schedule-title"
            name="success-step-schedule-title"
            as="h2" 
            className="mb-2"
          >
            Congratulations, {firstName}!
          </Title>
          <div className="text-2xl font-bold text-[#b3905e] mb-4">
            You're eligible for up to {formatCurrency(debtAmount)}
          </div>
        </div>
        <ScheduleCallForm 
          firstName={firstName}
          phoneNumber={customerPhoneNumber}
          onSchedule={handleScheduleCall} 
        />
        {error && (
          <ErrorMessage 
            message={error}
            className="mt-4"
          />
        )}
        {showScheduleForm && (
          <Button
            id="success-step-back"
            name="success-step-back"
            onClick={() => setShowScheduleForm(false)}
            variant="outline"
            size="default"
            fullWidth
            className="mt-4"
          >
            Back to Contact Options
          </Button>
        )}
      </>
    );
  }

  // Create component renderer with callbacks
  const renderComponent = createComponentRenderer({
    customerPhoneNumber,
    onScheduleClick: () => setShowScheduleForm(true),
    onCallInitiated: handleCallInitiated,
    onCountdownComplete: handleCountdownComplete,
    onInitiateCall,
    messageTitle: variant.messageConfig?.title,
    messageDescription: variant.messageConfig?.description,
    variantId: variant.id, // Pass variant ID for conditional rendering
    dialerDuration: variant.dialerDuration, // Pass dialer duration from variant config
  });

  return (
    <div id="success-step" className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-[#b3905e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-[#b3905e]" />
        </div>
        
        <Title 
          id="success-step-title"
          name="success-step-title"
          as="h2" 
          className="mb-2"
        >
          {scheduledTime ? 'Application Received!' : `Congratulations, ${firstName}!`}
        </Title>
        <Description 
          id="success-step-description"
          name="success-step-description"
          size="lg"
        >
          {scheduledTime ? (
            <span>
              Thank you <span className="font-medium">{firstName}</span>, we look forward to speaking with you on{' '}
              <span className="font-medium">{formatAppointmentTime(scheduledTime)}</span>
            </span>
          ) : (
            <div className="text-2xl font-bold text-[#b3905e]">
              You're eligible for up to {formatCurrency(debtAmount)}
            </div>
          )}
        </Description>
      </div>
      
      <div className="w-full md:w-2/3 mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
          {variant.components.map(renderComponent)}
        </div>
      </div>
    </div>
  );
}
